import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { apiCallable } from '@/lib/api'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Users, Check, User, Search, Loader2 } from 'lucide-react'
import type { Person } from '@/types'

interface MergePersonDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  sourcePerson: Person | null
  groupId: string
  onMerged: () => void
}

export function MergePersonDialog({
  open,
  onOpenChange,
  sourcePerson,
  groupId,
  onMerged,
}: MergePersonDialogProps) {
  const { t } = useTranslation()
  const [persons, setPersons] = useState<Person[]>([])
  const [loading, setLoading] = useState(false)
  const [searching, setSearching] = useState(false)
  const [loadingData, setLoadingData] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedPersonId, setSelectedPersonId] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState('')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fetchPersons = useCallback(async (search?: string) => {
    if (!groupId) return

    const isInitialLoad = !search
    if (isInitialLoad) {
      setLoadingData(true)
    } else {
      setSearching(true)
    }

    try {
      const getPersonsFn = apiCallable('getPersons')
      const params: Record<string, unknown> = { groupId, pageSize: 50 }
      if (search) {
        params.search = search
      }
      const result = await getPersonsFn(params)
      const data = result.data as { success: boolean; persons: Person[] }

      if (data.success) {
        setPersons(data.persons.filter(p => p.id !== sourcePerson?.id))
      }
    } catch (err) {
      console.error('Failed to load persons:', err)
      setError(t('persons.failedToLoad'))
    } finally {
      if (isInitialLoad) {
        setLoadingData(false)
      } else {
        setSearching(false)
      }
    }
  }, [groupId, sourcePerson?.id])

  useEffect(() => {
    if (open) {
      fetchPersons()
      setSelectedPersonId('')
      setSearchQuery('')
      setError(null)
    }
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [open, fetchPersons])

  useEffect(() => {
    if (!open) return

    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (!searchQuery) {
      // Reset to initial list when search is cleared
      fetchPersons()
      return
    }

    debounceRef.current = setTimeout(() => {
      fetchPersons(searchQuery)
    }, 300)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [searchQuery, open, fetchPersons])

  const handleMerge = async () => {
    if (!selectedPersonId || !sourcePerson) return

    setLoading(true)
    setError(null)
    try {
      const mergeFn = apiCallable('mergePersons')
      await mergeFn({
        groupId,
        sourcePersonId: sourcePerson.id,
        targetPersonId: selectedPersonId,
      })

      onMerged()
      onOpenChange(false)
    } catch (err) {
      console.error('Failed to merge persons:', err)
      setError(t('merge.failedToMerge'))
    } finally {
      setLoading(false)
    }
  }

  const selectedPerson = persons.find(p => p.id === selectedPersonId)

  if (!sourcePerson) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('merge.title')}</DialogTitle>
          <DialogDescription>
            {t('merge.description', { name: sourcePerson.name || t('common.unknown') })}
          </DialogDescription>
        </DialogHeader>

        {/* Source person */}
        <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
          <div className="w-12 h-12 rounded overflow-hidden bg-paper-2">
            {sourcePerson.thumbnailUrl ? (
              <img
                src={sourcePerson.thumbnailUrl}
                alt={sourcePerson.name || t('common.unknown')}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                <User className="h-5 w-5" />
              </div>
            )}
          </div>
          <div>
            <div className="font-medium">{sourcePerson.name || t('common.unknown')}</div>
            <div className="text-xs text-muted-foreground">
              {t('persons.sightings', { count: sourcePerson.totalSightings })}
            </div>
          </div>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loadingData ? (
          <div className="py-8 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>{t('merge.searchTarget')}</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('merge.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8"
                />
                {searching && (
                  <Loader2 className="absolute right-2.5 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />
                )}
              </div>
            </div>

            <div className="max-h-60 overflow-y-auto space-y-2">
              {persons.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {t('merge.noPersons')}
                </p>
              ) : (
                persons.map((person) => (
                  <Card
                    key={person.id}
                    className={`cursor-pointer transition-colors ${
                      selectedPersonId === person.id
                        ? 'border-primary bg-primary/5'
                        : 'hover:border-primary/50'
                    }`}
                    onClick={() => setSelectedPersonId(person.id)}
                  >
                    <CardContent className="p-3 flex items-center gap-3">
                      <div className="w-10 h-10 rounded overflow-hidden bg-paper-2">
                        {person.thumbnailUrl ? (
                          <img
                            src={person.thumbnailUrl}
                            alt={person.name || t('common.unknown')}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                            <Users className="h-4 w-4" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium">
                          {person.name || t('common.unknown')}
                        </div>
                        {person.classificationColor && (
                          <div className="flex items-center gap-1">
                            <div
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: person.classificationColor }}
                            />
                            <span
                              className="text-xs"
                              style={{ color: person.classificationColor }}
                            >
                              {person.classificationName}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {t('persons.sightings', { count: person.totalSightings })}
                      </div>
                      {selectedPersonId === person.id && (
                        <Check className="h-5 w-5 text-primary" />
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleMerge}
            disabled={!selectedPersonId || loading || loadingData}
          >
            {loading ? t('merge.merging') : t('merge.mergeInto', { name: selectedPerson?.name || t('common.unknown') })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
